// Gate D2.3, accessibility (engine/docs/spikes/002-green-valley/brief.md; decision 0063), beside the A4 settings:
// - text size and contrast reach every screen (the UI's own root) and every overlay outside it (the HUD, subtitles,
//   captions): the UI's custom properties are copied onto the document's root, with `data-contrast` there for the
//   HUD's own rules;
// - subtitles from `dialogue:line` (always on: dialogue is text), the speaker's name and the line, held at least the
//   table's minimum (a `log` region); sound captions from the audio's caption events (a `log` region too) ("[growl, left]"), a few at once, repeats merged,
//   shown while the captions setting is on (the events are counted either way);
// - the objective marker and off-screen attacker indicator toggles (settings the HUD reads);
// - a recap on load: after a slot loads, play opens paused under a "Previously" screen (the objective and the last
//   few things that happened: the story's objective and event log, recap.ts);
// - pause anywhere: the pause action lives in a base `system` context above dialogue (contexts.json), so it works in a
//   cutscene; the story's dialogue box (a screen: the menu context) takes back as pause (src/story/ui.ts); on touch,
//   while the touch overlay is hidden by such a context, a pause button shows;
// - the flash cap: no more than the setting's number of flashes start in any second (phase B's flashes);
// - for the checks and shots, a line said on the kernel bus as the story's runner says it (`say`), and a talk with a
//   townsperson through the story where the page has one (`talk`).
// Planted defects: `flash-cap-ignored` (@rpg/vfx's): every hit flashes; the flash-cap check must go red. The caption
// check's (`important-sound-no-caption`) is the audio's.
// Since the composition framework's flow stream all of this but the recap's source (./recap.ts, the story's) and the
// planted defect's switch is the accessibility-options feature (@rpg-unit/accessibility-options), with its numbers
// from @rpg-unit/standard-settings' accessibility table; this file passes the spike's ports in.
import { mountAccessibilityOptions, type recapScreen } from "@rpg-unit/accessibility-options";
import type { Settings, Ui } from "@rpg/ui";
import { FLASH_FAULTS } from "@rpg/vfx";
import type { CaptionEvent } from "@rpg/audio";
import type { EventBus } from "@rpg/kernel";
import type { InputContexts, Runtime } from "@rpg/runtime";
import type { FlowHandle } from "../boot";
import type { SpikeContent } from "../content";
import type { AccessibilityRow } from "../content/types.gen";
import type { GameEvents } from "../core";
import type { FaultGroup } from "../faults";
import { FLASH_CAP } from "../feedback";
import type { SpikeSaves } from "../save";
import { storyRecap, type RecapStory } from "./recap";

export type { RecapSource } from "./recap";
export { recapScreen } from "@rpg-unit/accessibility-options";

/** The planted defects this module owns. */
export const A11Y_FAULT_NAMES = ["flash-cap-ignored"] as const;

/** The fault group (`?faults=flash-cap-ignored` read on creation). */
export function a11yFaultGroup(search = typeof location !== "undefined" ? location.search : ""): FaultGroup {
  if ((new URLSearchParams(search).get("faults") ?? "").split(",").includes("flash-cap-ignored")) FLASH_FAULTS.capIgnored = true;
  return { names: A11Y_FAULT_NAMES, get: () => FLASH_FAULTS.capIgnored, set: (_n, on) => void (FLASH_FAULTS.capIgnored = on) };
}

/** What accessibility needs from the page. */
export interface A11yDeps {
  runtime: Runtime;
  content: SpikeContent;
  events: EventBus<GameEvents>;
  ui: Ui;
  settings: Settings;
  flow: FlowHandle;
  saves: SpikeSaves;
  contexts: InputContexts;
  /** The audio's caption events. */
  onCaption: (fn: (c: CaptionEvent) => void) => void;
  /** The world's region under the Wanderer (the recap's last line), or null. */
  region: () => string | null;
  /** The story (phase D joined): the recap's objective and deeds; null in a page with none. */
  story: () => RecapStory | null;
  /** Talk with a townsperson as a press on them would (the story's), or null in a page with no story. */
  talk: ((npc: string) => void) | null;
  /** A speaker's display name (a string id) where the page knows one (the story's: the npcs table), else null. */
  speakerName?: (speaker: string) => string | null;
  /** Whether the story's dialogue box shows the conversation now (its lines are on screen there already). */
  boxOpen?: () => boolean;
  /** Whether touch is the device in use. */
  touch: () => boolean;
  /** Binds the recap screen registered with the UI. */
  recap: ReturnType<typeof recapScreen>;
}

/** Mount accessibility: the accessibility-options feature over the spike's content, bus, UI, flow, saves and story. */
export function mountAccessibility(d: A11yDeps) {
  const row = d.content.registry.get("accessibility", "default") as AccessibilityRow | undefined;
  if (!row) throw new Error("accessibility: the accessibility table has no 'default' row");
  return mountAccessibilityOptions({
    defaults: row,
    t: d.content.t,
    has: (id) => d.content.strings.has(id),
    // the standard dialogue events on the kernel's bus
    events: { on: (name, fn) => d.events.on(name, fn), emit: (name, payload) => d.events.emit(name, payload as never) },
    ui: d.ui,
    settings: d.settings,
    flow: d.flow,
    onLoaded: (fn) => d.saves.onLoaded(fn),
    contexts: d.contexts,
    onCaption: d.onCaption,
    runtime: d.runtime,
    setFlashCap: (n) => void (FLASH_CAP.perSecond = n),
    flashCap: () => FLASH_CAP.perSecond,
    recap: d.recap,
    recapSource: storyRecap({ content: d.content, story: d.story, region: d.region }),
    ...(d.speakerName ? { speakerName: d.speakerName } : {}),
    ...(d.boxOpen ? { boxOpen: d.boxOpen } : {}),
    touch: d.touch,
    talk: d.talk,
  });
}
